import { saveAs } from 'file-saver-es';
import { map, mergeMap, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { RouterSelectors, ToastService } from '@mitsubishi/core';
import { UpgrageModelDialogComponent } from '@mitsubishi/shared/components';
import {
  ApmResourceIds,
  RequestLogRequest,
  RequestLogService,
  SelectionRequestLogScope,
} from '@mitsubishi/shared/data-access';
import { DialogService } from '@mitsubishi/ui/dialog';
import { FileTransferService, MiscUtils } from '@mitsubishi/utils';

import { EntityCollections } from '@angular-ru/cdk/entity';
import { Navigate } from '@ngxs/router-plugin';
import { Action, State, StateContext, Store } from '@ngxs/store';

import { Selection } from '../../models';
import {
  AccessoryService,
  AlternativeExtraService,
  AlternativeService,
  DatasheetService,
  SelectionService,
  UnitService,
} from '../../services';
import { SelectionActions } from './selections.actions';

/* Questo State serve solo per gestire le azioni legate alle Quote e non ha uno stato proprio. */
@State({
  name: 'selectionsActions',
})
@Injectable()
export class ProjectSelectionsState {
  constructor(
    private store: Store,
    private selectionService: SelectionService,
    private toastService: ToastService,
    private unitService: UnitService,
    private router: Router,
    private alternativeService: AlternativeService,
    private alternativeExtraService: AlternativeExtraService,
    private datasheetService: DatasheetService,
    private accessoryService: AccessoryService,
    private requestLogService: RequestLogService,
    private dialogService: DialogService,
    private fileTransferService: FileTransferService
  ) {}

  @Action(SelectionActions.Open)
  open(ctx: StateContext<EntityCollections<Selection>>, { selectionId }: SelectionActions.Open) {
    const projectId = this.currentProjectId;
    return ctx.dispatch(new Navigate(['project', projectId, 'selection', selectionId]));
  }

  @Action(SelectionActions.Create)
  createQuote(ctx: StateContext<EntityCollections<Selection>>, { projectId, organizationId }: SelectionActions.Create) {
    return this.selectionService.createQuote(projectId, organizationId).pipe(
      map(response => {
        return ctx.dispatch(new SelectionActions.Open(response.uuid));
      })
    );
  }

  @Action(SelectionActions.Duplicate)
  duplicateQuote(ctx: StateContext<EntityCollections<Selection>>, { selectionId }: SelectionActions.Duplicate) {
    const projectId = this.currentProjectId;
    return this.selectionService.duplicateQuote(projectId, selectionId).pipe(
      mergeMap(response => {
        return ctx.dispatch(new SelectionActions.Open(response.uuid));
      })
    );
  }

  @Action(SelectionActions.Delete)
  deleteQuote(ctx: StateContext<EntityCollections<Selection>>, { selectionId }: SelectionActions.Delete) {
    const projectId = this.currentProjectId;
    return this.selectionService.deleteQuote(projectId, selectionId).pipe(
      mergeMap(() => {
        this.toastService.showToast({ resourceId: '444954' });
        return ctx.dispatch(new Navigate(['project', projectId]));
      })
    );
  }

  @Action(SelectionActions.Update)
  update(ctx: StateContext<EntityCollections<Selection>>, { selectionId, title }: SelectionActions.Update) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateQuote(projectId, selectionId, title);
  }

  @Action(SelectionActions.UpdateCurrencyMultiplier)
  updateCurrencyMultiplier(
    ctx: StateContext<EntityCollections<Selection>>,
    { currencyMultiplier }: SelectionActions.UpdateCurrencyMultiplier
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateCurrencyMultiplier(projectId, this.currentSelectionId, currencyMultiplier);
  }

  @Action(SelectionActions.UpdateGlobalPriceMultiplier)
  updateGlobalPriceMultiplier() {
    const projectId = this.currentProjectId;
    return this.selectionService.updateGlobalPriceMultiplier(projectId, this.currentSelectionId);
  }

  @Action(SelectionActions.UpdatePriceMultiplier)
  updatePriceMultiplier(
    ctx: StateContext<EntityCollections<Selection>>,
    { priceMultiplier }: SelectionActions.UpdatePriceMultiplier
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.updatePriceMultiplier(projectId, this.currentSelectionId, priceMultiplier);
  }

  @Action(SelectionActions.UpdateCustomCurrency)
  updateCustomCurrency(
    ctx: StateContext<EntityCollections<Selection>>,
    { customCurrency }: SelectionActions.UpdateCustomCurrency
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateCustomCurrency(projectId, this.currentSelectionId, customCurrency);
  }

  @Action(SelectionActions.ToggleUseCustomCurrency)
  toggleUseCustomCurrency(ctx: StateContext<EntityCollections<Selection>>) {
    const projectId = this.currentProjectId;
    return this.selectionService.toggleUseCustomCurrency(projectId, this.currentSelectionId);
  }

  @Action(SelectionActions.ToggleIsHot)
  toggleIsHot(ctx: StateContext<EntityCollections<Selection>>) {
    const projectId = this.currentProjectId;
    return this.selectionService.toggleIsHot(projectId, this.currentSelectionId);
  }

  @Action(SelectionActions.Consolidate)
  consolidate(
    ctx: StateContext<EntityCollections<Selection>>,
    { projectId, selectionId }: SelectionActions.Consolidate
  ) {
    return this.selectionService.updateSelectionState(projectId, selectionId, 'CONSOLIDATE');
  }

  @Action(SelectionActions.DraftQuote)
  draftQuote(ctx: StateContext<EntityCollections<Selection>>, { projectId, selectionId }: SelectionActions.DraftQuote) {
    return this.selectionService
      .updateSelectionState(projectId, selectionId, 'SET_DRAFT')
      .pipe(mergeMap(() => ctx.dispatch(new SelectionActions.CheckModelsHasUpgrade())));
  }

  @Action(SelectionActions.ConfirmAlternative)
  confirmAlternative(ctx: StateContext<EntityCollections<Selection>>, { projectId, selectionId, alternativeId }: SelectionActions.ConfirmAlternative) {
    return this.selectionService
      .updateSelectionState(projectId, selectionId, 'CONFIRM_ALTERNATIVE', {alternativeId});
  }

  @Action(SelectionActions.EditNote)
  confirm(
    ctx: StateContext<EntityCollections<Selection>>,
    { projectId, selectionId, note }: SelectionActions.EditNote
  ) {
    return this.selectionService.editNote(projectId, selectionId, note);
  }

  @Action(SelectionActions.UpdateOrderFields)
  updateOrderFields(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, orderFields }: SelectionActions.UpdateOrderFields
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateOrderFields(projectId, selectionId, orderFields).pipe(
      tap({
        next: () => this.showSaveSuccessToast(),
      })
    );
  }

  @Action(SelectionActions.EditAlternativeDescription)
  editAlternativeDescription(
    ctx: StateContext<EntityCollections<Selection>>,
    { projectId, selectionId, alternativeId, note }: SelectionActions.EditAlternativeDescription
  ) {
    return this.selectionService.editAlternativeDescription(projectId, selectionId, alternativeId, note);
  }

  @Action(SelectionActions.UnitSoftDelete)
  unitSoftDelete(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId }: SelectionActions.UnitSoftDelete
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.softDeleteUnit(projectId, selectionId, unitId);
  }

  @Action(SelectionActions.UnitRestore)
  unitRestore(ctx: StateContext<EntityCollections<Selection>>, { selectionId, unitId }: SelectionActions.UnitRestore) {
    const projectId = this.currentProjectId;
    return this.unitService.restoreUnit(projectId, selectionId, unitId);
  }

  @Action(SelectionActions.UnitDelete)
  unitDelete(ctx: StateContext<EntityCollections<Selection>>, { selectionId, unitId }: SelectionActions.UnitRestore) {
    const projectId = this.currentProjectId;
    return this.unitService.deleteUnit(projectId, selectionId, unitId);
  }

  @Action(SelectionActions.CreateUnitFromTemplate)
  createUnitFromTemplate(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, alternativeId, entityId, entityType }: SelectionActions.CreateUnitFromTemplate
  ) {
    const projectId = this.currentProjectId;
    return this.unitService
      .createUnitFromTemplate(projectId, selectionId, alternativeId, entityId, entityType)
      .pipe(mergeMap(() => ctx.dispatch(new Navigate(['project', projectId, 'selection', selectionId]))));
  }

  @Action(SelectionActions.CreateUnitFromBleen)
  createUnitFromBleen(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, alternativeId, bleenId }: SelectionActions.CreateUnitFromBleen
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.createUnitFromBleen(projectId, selectionId, alternativeId, bleenId).pipe(
      tap(() => {
        this.toastService.showToast({ resourceId: '445227' });
      })
    );
  }

  @Action(SelectionActions.CreateUnitAndConfigFromBleen)
  createUnitAndConfigFromBleenn(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, alternativeId, bleenId }: SelectionActions.CreateUnitAndConfigFromBleen
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.createUnitFromBleen(projectId, selectionId, alternativeId, bleenId).pipe(
      tap(({ uuid }) => {
        this.toastService.showToast({ resourceId: '445227' });
        window.open(
          '#/' +
            this.router.serializeUrl(
              this.router.createUrlTree(['project', projectId, 'selection', selectionId, 'unit', uuid, 'configuration'])
            ) +
            '?newTab=true',
          '_blank'
        );
      })
    );
  }

  @Action(SelectionActions.UnitDuplicate)
  unitDuplicate(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId }: SelectionActions.UnitDuplicate
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.duplicateUnit(projectId, selectionId, unitId);
  }

  @Action(SelectionActions.UnitMove)
  unitMove(ctx: StateContext<EntityCollections<Selection>>, { unitId, position }: SelectionActions.UnitMove) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService.moveUnit(projectId, selectionId, unitId, { position });
  }

  @Action(SelectionActions.MoveUnitCustom)
  moveUnitCustom(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, position }: SelectionActions.MoveUnitCustom
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService.moveUnitCustom(projectId, selectionId, unitId, { position });
  }

  @Action(SelectionActions.UpdateUnitQuantity)
  updateUnitQuantity(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, alternativeId, quantity }: SelectionActions.UpdateUnitQuantity
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService.updateQuantity(projectId, selectionId, unitId, alternativeId, quantity);
  }

  @Action(SelectionActions.UpdateUnitCustomQuantity)
  updateUnitCustomQuantity(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, alternativeId, quantity }: SelectionActions.UpdateUnitCustomQuantity
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService.updateQuantityCustom(projectId, selectionId, unitId, alternativeId, quantity);
  }

  @Action(SelectionActions.AlternativeCreate)
  alternativeCreate(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId }: SelectionActions.AlternativeCreate
  ) {
    const projectId = this.currentProjectId;
    return this.alternativeService.createAlternative(projectId, selectionId);
  }

  @Action(SelectionActions.AlternativeDelete)
  alternativeDelete(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, alternativeId }: SelectionActions.AlternativeDelete
  ) {
    const projectId = this.currentProjectId;
    return this.alternativeService.deleteAlternative(projectId, selectionId, alternativeId);
  }

  @Action(SelectionActions.AlternativePricelist)
  toggleFuturePricelist(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId }: SelectionActions.AlternativePricelist
  ) {
    const projectId = this.currentProjectId;
    return this.alternativeService.toggleFuturePricelist(projectId, selectionId, unitId);
  }

  @Action(SelectionActions.AlternativeUpdatePriceForCustomer)
  updatePriceForCustomer(
    ctx: StateContext<EntityCollections<Selection>>,
    { alternativeId, priceForCustomer }: SelectionActions.AlternativeUpdatePriceForCustomer
  ) {
    const projectId = this.currentProjectId;
    return this.alternativeService.updatePriceForCustomer(
      projectId,
      this.currentSelectionId,
      alternativeId,
      priceForCustomer
    );
  }

  @Action(SelectionActions.AlternativeMoveRow)
  moveAlternativeRow(
    ctx: StateContext<EntityCollections<Selection>>,
    { alternativeId, moveChildRequest }: SelectionActions.AlternativeMoveRow
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.alternativeService.moveAlternativeRow(projectId, selectionId, alternativeId, moveChildRequest);
  }

  @Action(SelectionActions.UnitAlternativeLink)
  linkUnitAlternative(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId, alternativeId }: SelectionActions.UnitAlternativeLink
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.linkUnitAlternative(projectId, selectionId, unitId, alternativeId);
  }

  @Action(SelectionActions.UnitAlternativeUnlink)
  unlinkUnitAlternative(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId, alternativeId }: SelectionActions.UnitAlternativeUnlink
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.unlinkUnitAlternative(projectId, selectionId, unitId, alternativeId);
  }

  @Action(SelectionActions.UnitCustomAlternativeLink)
  linkUnitCustomAlternative(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId, alternativeId }: SelectionActions.UnitCustomAlternativeLink
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.linkUnitCustomAlternative(projectId, selectionId, unitId, alternativeId);
  }

  @Action(SelectionActions.UnitCustomAlternativeUnlink)
  unlinkUnitCustomAlternative(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId, alternativeId }: SelectionActions.UnitCustomAlternativeUnlink
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.unlinkUnitCustomAlternative(projectId, selectionId, unitId, alternativeId);
  }

  @Action(SelectionActions.AlternativeExtraCreate)
  createAlternativeExtra(
    ctx: StateContext<EntityCollections<Selection>>,
    { alternativeId }: SelectionActions.AlternativeExtraCreate
  ) {
    const projectId = this.currentProjectId;
    return this.alternativeExtraService.createAlternativeExtra(projectId, this.currentSelectionId, alternativeId);
  }

  @Action(SelectionActions.AlternativeExtraDelete)
  deleteAlternativeExtra(
    ctx: StateContext<EntityCollections<Selection>>,
    { alternativeExtraId }: SelectionActions.AlternativeExtraDelete
  ) {
    const projectId = this.currentProjectId;
    return this.alternativeExtraService.deleteAlternativeExtra(projectId, this.currentSelectionId, alternativeExtraId);
  }

  @Action(SelectionActions.AlternativeExtraUpdateCustomerPrice)
  updateAlternativeExtraSelling(
    ctx: StateContext<EntityCollections<Selection>>,
    { alternativeExtraId, customer }: SelectionActions.AlternativeExtraUpdateCustomerPrice
  ) {
    const projectId = this.currentProjectId;
    return this.alternativeExtraService.updateCustomerPrice(
      projectId,
      this.currentSelectionId,
      alternativeExtraId,
      customer
    );
  }

  @Action(SelectionActions.AlternativeExtraUpdatePrice)
  updateAlternativeExtraCost(
    ctx: StateContext<EntityCollections<Selection>>,
    { alternativeExtraId, price }: SelectionActions.AlternativeExtraUpdatePrice
  ) {
    const projectId = this.currentProjectId;
    return this.alternativeExtraService.updatePrice(projectId, this.currentSelectionId, alternativeExtraId, price);
  }

  @Action(SelectionActions.AlternativeExtraUpdateName)
  updateAlternativeExtraName(
    ctx: StateContext<EntityCollections<Selection>>,
    { alternativeExtraId, name }: SelectionActions.AlternativeExtraUpdateName
  ) {
    const projectId = this.currentProjectId;
    return this.alternativeExtraService.updateName(projectId, this.currentSelectionId, alternativeExtraId, name);
  }

  @Action(SelectionActions.ShareOffer)
  shareOffer(
    ctx: StateContext<EntityCollections<Selection>>,
    { email, priceVisibility, noteVisibility, datasheetVisibility, documentVisibility }: SelectionActions.ShareOffer
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.selectionService
      .shareOffer(projectId, selectionId, {
        email,
        priceVisibility,
        noteVisibility,
        datasheetVisibility,
        documentVisibility,
      })
      .pipe(tap(() => this.toastService.showToast({ resourceId: '444990' })));
  }

  @Action(SelectionActions.ExportOfferXls)
  exportOfferXls(
    ctx: StateContext<EntityCollections<Selection>>,
    { fileName, priceType }: SelectionActions.ExportOfferXls
  ) {
    const selectionId = this.currentSelectionId;
    return this.selectionService.exportOfferCsv(selectionId, priceType).pipe(
      tap(response => {
        const blob = new Blob([response.body as Blob], { type: 'application/octet-stream;charset=UTF-8' });
        saveAs(blob, MiscUtils.extractFileNameFromHttpResponse(response) ?? `${fileName}.xlsx`);
      })
    );
  }

  @Action(SelectionActions.OpenShared)
  openSharedOffer() {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return window.open(`#/project/${projectId}/selection/${selectionId}/shared`, '_blank');
  }

  @Action(SelectionActions.UpdateOfferRecipient)
  updateOfferRecipient(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, offerRecipient }: SelectionActions.UpdateOfferRecipient
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateOfferRecipient(projectId, selectionId, offerRecipient);
  }

  @Action(SelectionActions.UpdateAddress)
  updateAddress(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, recipientAddress }: SelectionActions.UpdateAddress
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateOfferAddress(projectId, selectionId, recipientAddress);
  }

  @Action(SelectionActions.AddAdditionalInfo)
  addAdditionalInfo(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, title, description, inEvidence, categoryId, templateId }: SelectionActions.AddAdditionalInfo
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.addAdditionalInfo(
      projectId,
      selectionId,
      title,
      description,
      inEvidence,
      categoryId,
      templateId
    );
  }

  @Action(SelectionActions.UpdateAdditionalInfo)
  updateAdditionalInfo(
    ctx: StateContext<EntityCollections<Selection>>,
    {
      selectionId,
      additionalInfoId,
      title,
      description,
      inEvidence,
      categoryId,
      templateId,
    }: SelectionActions.UpdateAdditionalInfo
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateAdditionalInfo(
      projectId,
      selectionId,
      additionalInfoId,
      title,
      description,
      inEvidence,
      categoryId,
      templateId
    );
  }

  @Action(SelectionActions.DeleteAdditionalInfo)
  deleteAdditionalInfo(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, additionalInfoId }: SelectionActions.DeleteAdditionalInfo
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.deleteAdditionalInfo(projectId, selectionId, additionalInfoId);
  }

  @Action(SelectionActions.FinalizeVersion)
  finalizeVersion(ctx: StateContext<EntityCollections<Selection>>, { selectionId }: SelectionActions.FinalizeVersion) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateSelectionState(projectId, selectionId, 'FINALIZE_OFFER');
  }

  @Action(SelectionActions.CreateRevision)
  createRevision(ctx: StateContext<EntityCollections<Selection>>, { selectionId }: SelectionActions.CreateRevision) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateSelectionState(projectId, selectionId, 'CREATE_REVISION');
  }

  @Action(SelectionActions.SetStatisticData)
  setStatisticData(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, statisticData }: SelectionActions.SetStatisticData
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.setStatisticData(projectId, selectionId, statisticData);
  }

  @Action(SelectionActions.UpdatePriceValidityDate)
  updatePriceValidityDate(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, priceValidityDate }: SelectionActions.UpdatePriceValidityDate
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.putPriceValidityDate(projectId, selectionId, priceValidityDate);
  }

  @Action(SelectionActions.ToggleTestPriceList)
  toggleTestPriceList(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId }: SelectionActions.ToggleTestPriceList
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.toggleTestPriceList(projectId, selectionId);
  }

  @Action(SelectionActions.UpdateExpiringDate)
  updateExpiringDate(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, expiringDate }: SelectionActions.UpdateExpiringDate
  ) {
    const projectId = this.currentProjectId;
    return this.selectionService.putExpiringDate(projectId, selectionId, expiringDate);
  }

  @Action(SelectionActions.DatasheetCreatePdf)
  datasheetCreatePdf(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, requestTraceId }: SelectionActions.DatasheetCreatePdf
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.datasheetService.generateSelectionUnitDatasheetPDF(projectId, selectionId, unitId, requestTraceId);
  }

  @Action(SelectionActions.DatasheetDownloadPdf)
  datasheetDownloadPdf(
    ctx: StateContext<EntityCollections<Selection>>,
    { fileName, customFileName }: SelectionActions.DatasheetDownloadPdf
  ) {
    return this.datasheetService.exportUnitDatasheetPDF(fileName).pipe(
      tap(async ({ url }) => {
        if (customFileName) {
          const extension = fileName.split('.');
          const blob = await fetch(url).then(r => r.blob());
          saveAs(blob, customFileName + '.' + extension[1]);
        } else {
          window.open(url);
        }
      })
    );
  }

  @Action(SelectionActions.ConfirmOrder)
  confirmOrder(ctx: StateContext<EntityCollections<Selection>>, { selectionId }: SelectionActions.ConfirmOrder) {
    const projectId = this.currentProjectId;
    return this.selectionService.updateSelectionState(projectId, selectionId, 'SEND_ORDER', { sendImmediate: true });
  }

  @Action(SelectionActions.UpdateAccessoryQuantity)
  updateAccessoryQuantity(
    ctx: StateContext<EntityCollections<Selection>>,
    { accessoryId, quantity }: SelectionActions.UpdateAccessoryQuantity
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.accessoryService.updateAccessoryQuantity(projectId, selectionId, accessoryId, quantity);
  }

  @Action(SelectionActions.GoToUnitConfiguration)
  goToUnitConfiguration(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId }: SelectionActions.GoToUnitConfiguration
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService
      .duplicateSession(projectId, selectionId, unitId)
      .pipe(
        mergeMap(() =>
          ctx.dispatch(new Navigate(['project', projectId, 'selection', selectionId, 'unit', unitId, 'configuration']))
        )
      );
  }

  @Action(SelectionActions.CustomUnitCreate)
  customUnitCreate(ctx: StateContext<EntityCollections<Selection>>, { unitCustom }: SelectionActions.CustomUnitCreate) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService
      .createCustomUnit(projectId, selectionId, unitCustom)
      .pipe(tap(result => ctx.dispatch(new SelectionActions.CustomUnitCreateSuccess(unitCustom, result.uuid))));
  }

  @Action(SelectionActions.CustomUnitEdit)
  customUnitEdit(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, unitCustom }: SelectionActions.CustomUnitEdit
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService.editCustomUnit(projectId, selectionId, unitId, unitCustom);
  }

  @Action(SelectionActions.UnitCustomSoftDelete)
  unitCustomSoftDelete(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId }: SelectionActions.UnitCustomSoftDelete
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.softDeleteUnitCustom(projectId, selectionId, unitId);
  }

  @Action(SelectionActions.UnitCustomRestore)
  restoreUnitCustom(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId }: SelectionActions.UnitCustomRestore
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.restoreUnitCustom(projectId, selectionId, unitId);
  }

  @Action(SelectionActions.UnitCustomDelete)
  unitCustomDelete(
    ctx: StateContext<EntityCollections<Selection>>,
    { selectionId, unitId }: SelectionActions.UnitCustomDelete
  ) {
    const projectId = this.currentProjectId;
    return this.unitService.deleteUnitCustom(projectId, selectionId, unitId);
  }

  @Action(SelectionActions.CreateAccessoryCustom)
  createCustomUnit(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, accessoryCustom, isCustomUnit }: SelectionActions.CreateAccessoryCustom
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.accessoryService
      .createAccessoryCustom(projectId, selectionId, unitId, accessoryCustom, isCustomUnit)
      .pipe(
        tap(result =>
          ctx.dispatch(new SelectionActions.CustomAccessoryCreateSuccess(unitId, isCustomUnit, result.uuid))
        )
      );
  }

  @Action(SelectionActions.UpdateAccessoryCustom)
  updateAccessoryCustomQuantity(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, acessory, isCustomUnit }: SelectionActions.UpdateAccessoryCustom
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.accessoryService.updateAccessoryCustom(projectId, selectionId, unitId, acessory, isCustomUnit);
  }

  @Action(SelectionActions.DeleteAccessoryCustom)
  deleteAccessoryCustom(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, acessoryId, isCustomUnit }: SelectionActions.DeleteAccessoryCustom
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.accessoryService.deleteAccessoryCustom(projectId, selectionId, unitId, acessoryId, isCustomUnit);
  }

  @Action(SelectionActions.UpdateUnitWarranty)
  updateUnitWarranty(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, warrantyCode, alternativeId }: SelectionActions.UpdateUnitWarranty
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService.updateWarranty(projectId, selectionId, unitId, alternativeId, warrantyCode);
  }

  @Action(SelectionActions.UpdateCustomUnitWarranty)
  updateCustomUnitWarranty(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, warrantyCode, alternativeId }: SelectionActions.UpdateCustomUnitWarranty
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService.updateUnitCustomWarranty(projectId, selectionId, unitId, alternativeId, warrantyCode);
  }

  @Action(SelectionActions.DownloadWorkingConditionsRequestLog)
  downloadWorkingConditionsRequestLog(ctx: StateContext<EntityCollections<Selection>>) {
    const unitId = this.currentUnitId;
    const request: RequestLogRequest = {
      unitId: unitId,
      scope: SelectionRequestLogScope.WORKING_CONDITIONS,
    };
    return this.requestLogService.getRequestLog(request);
  }

  @Action(SelectionActions.DownloadPerformanceRequestLog)
  downloadPerformanceRequestLog(ctx: StateContext<EntityCollections<Selection>>) {
    const unitId = this.currentUnitId;
    const request: RequestLogRequest = {
      unitId: unitId,
      scope: SelectionRequestLogScope.PERFORMANCES,
    };
    return this.requestLogService.getRequestLog(request);
  }

  @Action(SelectionActions.UpdateOfferProbability)
  updateOfferProbability(
    ctx: StateContext<EntityCollections<Selection>>,
    { probability }: SelectionActions.UpdateOfferProbability
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.selectionService.updateOfferProbability(projectId, selectionId, probability);
  }

  @Action(SelectionActions.SetOfferLost)
  setOfferLost(ctx: StateContext<EntityCollections<Selection>>) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.selectionService.updateSelectionState(projectId, selectionId, 'LOST_OFFER');
  }

  @Action(SelectionActions.UpdateAccessoryPrice)
  updateAccessoryPrice(
    ctx: StateContext<EntityCollections<Selection>>,
    { accessoryId, price }: SelectionActions.UpdateAccessoryPrice
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.accessoryService.updatePrice(projectId, selectionId, accessoryId, price);
  }

  @Action(SelectionActions.AssignAccessories)
  assignAccessories(ctx: StateContext<EntityCollections<Selection>>, { answers }: SelectionActions.AssignAccessories) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    const unitId = this.currentUnitId;
    return this.unitService.assignAccessories(projectId, selectionId, unitId, answers);
  }

  @Action(SelectionActions.DownloadOrderLog)
  downloadOrderLog(ctx: StateContext<EntityCollections<Selection>>) {
    const selectionId = this.currentSelectionId;
    const request: RequestLogRequest = {
      selectionId: selectionId,
      scope: SelectionRequestLogScope.ORDER,
    };
    return this.requestLogService.getRequestLog(request);
  }

  @Action(SelectionActions.DownloadOfferPDF)
  downloadOfferPDF(_: StateContext<EntityCollections<Selection>>, body: SelectionActions.DownloadOfferPDF) {
    const selectionId = this.currentSelectionId;

    return this.selectionService.getOfferPdfFileUrl(selectionId, body).pipe(
      tap(fileUrl => {
        const fileName = MiscUtils.extractFileNameFromS3Url(fileUrl);
        this.fileTransferService.downloadFileWithOriginalName(fileUrl, fileName);
      })
    );
  }

  @Action(SelectionActions.DownloadOrderPDF)
  downloadOrderPDF(_) {
    const selectionId = this.currentSelectionId;
    return this.selectionService.downloadOrderPDF(selectionId).pipe(
      tap(response => {
        if (response.body === null) {
          return;
        }
        saveAs(response.body, MiscUtils.extractFileNameFromHttpResponse(response));
      })
    );
  }

  @Action(SelectionActions.AddExternalUnit)
  addExternalUnit(
    ctx: StateContext<EntityCollections<Selection>>,
    { bleenId, quantity, workingConditions, requestTraceId }: SelectionActions.AddExternalUnit
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    const unitId = this.currentUnitId;
    return this.unitService.addExternalUnit(
      projectId,
      selectionId,
      unitId,
      bleenId,
      quantity,
      workingConditions,
      requestTraceId
    );
  }

  @Action(SelectionActions.UpgradeModels)
  upgradeModels(ctx: StateContext<EntityCollections<Selection>>) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.selectionService.upgradeModels(projectId, selectionId);
  }

  @Action(SelectionActions.RefreshPrice)
  refreshPrice(ctx: StateContext<EntityCollections<Selection>>) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.selectionService.refreshPrice(projectId, selectionId);
  }

  @Action(SelectionActions.CheckModelsHasUpgrade)
  checkModelsHasUpgrade(ctx: StateContext<EntityCollections<Selection>>) {
    return this.selectionService.checkModelsHasUpgrade(this.currentProjectId, this.currentSelectionId).pipe(
      tap(selectionUnitUpdate => {
        if (selectionUnitUpdate && (selectionUnitUpdate.updated.length > 0 || selectionUnitUpdate.deleted.length > 0)) {
          this.dialogService.openDialogScroll(UpgrageModelDialogComponent, 'md', {
            selectionUnitUpdate: selectionUnitUpdate,
            onClose: () => {
              ctx.dispatch(new SelectionActions.UpgradeModels());
            },
          });
        }
      })
    );
  }

  get currentProjectId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).projectId;
  }

  get currentSelectionId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).selectionId;
  }

  get currentUnitId(): string {
    return this.store.selectSnapshot(RouterSelectors.params).unitId;
  }

  @Action(SelectionActions.UpdateUnitDescription)
  updateUnitDescription(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitId, description }: SelectionActions.UpdateUnitDescription
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService.updateDescription(projectId, selectionId, unitId, description);
  }

  @Action(SelectionActions.UpdateUnitCustomDescription)
  updateUnitCustomDescription(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitCustomId, description }: SelectionActions.UpdateUnitCustomDescription
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.unitService.updateUnitCustomDescription(projectId, selectionId, unitCustomId, description);
  }

  @Action(SelectionActions.DeleteAlternativeUnits)
  deleteAlternativeUnits(
    ctx: StateContext<EntityCollections<Selection>>,
    { unitIds, alternativeId }: SelectionActions.DeleteAlternativeUnits
  ) {
    const projectId = this.currentProjectId;
    const selectionId = this.currentSelectionId;
    return this.selectionService.deleteUnits(projectId, selectionId, alternativeId, unitIds);
  }

  private showSaveSuccessToast() {
    this.toastService.success({
      resourceId: ApmResourceIds.SAVE_SUCCESS,
    });
  }
}
